import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Icon, Modal, Grid, Input, Dropdown, Label, Message} from 'semantic-ui-react';
import {Actions} from '../../../common/actions';
import {isString, uniqBy} from 'lodash';
import {getDropdownOptions} from '../../../common/util/dropdown';
import moment from 'moment';

const {
  changeWtsEmployeeSelected, createWtsEmployee, updateWtsEmployee,
  getWtsPositions, getWtsProfiles, getWtsAgenciesDealer,
  createWtsUser, getWtsUser, clearWtsUser, updateWtsUser,
  createUserNotify, updateUserNotify, getWtsParams, getWtsEmployeesByRuc,
} = Actions;

const validateId = (id) => {
  if (typeof id !== 'string' || id.length !== 10) {
    return false;
  }
  const region = parseInt(id.substring(0, 2), 10);
  if (region < 1 || region > 24 || parseInt(id.substring(2, 3), 10) > 5) {
    return false;
  }
  const coefs = [2, 1, 2, 1, 2, 1, 2, 1, 2];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const val = parseInt(id.substring(i, i + 1), 10) * coefs[i];
    sum += val > 9 ? val - 9 : val;
  }
  const res = (sum % 10) === 0 ? 0 : 10 - (sum % 10);
  return res === parseInt(id.substring(9, 10), 10);
};

const fDate = (d, toISO) => {
  if (!d) {
    return '';
  }
  const date = moment(d, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601]);
  if (!date.isValid()) {
    return '';
  }
  return toISO ? date.format('YYYY-MM-DD') : date.format('DD/MM/YYYY');
};

const hasErrorInResponse = (obj) => {
  if (!obj) {
    return false;
  }
  if (obj.error) {
    return true;
  }
  if (obj.data && obj.data.error) {
    return true;
  }
  if (obj.data && obj.data.status && obj.data.status >= 400) {
    return true;
  }
  if (obj.status && typeof obj.status === 'number' && obj.status >= 400) {
    return true;
  }
  if (obj.response && obj.response.data && obj.response.data.error) {
    return true;
  }
  if (obj.response && obj.response.data && obj.response.data.data && obj.response.data.data.error) {
    return true;
  }
  return false;
};

const getErrorMsg = (errObj, defaultMsg) => {
  if (!errObj) {
    return defaultMsg;
  }
  if (typeof errObj === 'string') {
    return errObj;
  }
  if (errObj.response && errObj.response.data && errObj.response.data.data && errObj.response.data.data.error) {
    return errObj.response.data.data.error;
  }
  if (errObj.response && errObj.response.data && errObj.response.data.error) {
    return errObj.response.data.error;
  }
  if (errObj.data && errObj.data.error) {
    return errObj.data.error;
  }
  if (errObj.error) {
    return errObj.error;
  }
  if (errObj.message) {
    return errObj.message;
  }
  return defaultMsg;
};

const extractKcError = (wtsAuth, defaultMsg) => {
  if (wtsAuth.error) {
    const fromError = getErrorMsg(wtsAuth.error, null);
    if (fromError) {
      return fromError;
    }
  }
  if (wtsAuth.user && hasErrorInResponse(wtsAuth.user)) {
    const fromUser = getErrorMsg(wtsAuth.user, null);
    if (fromUser) {
      return fromUser;
    }
  }
  return defaultMsg;
};

const getMailsFromParam = (params, key) => {
  const param = (params || []).find((p) => p.codigo_llave === key);
  if (!param || !param.valor_texto) {
    return [];
  }
  return param.valor_texto.split(',').map((mail) => mail.trim()).filter(Boolean);
};

const buildBaseNotifyData = (payload, profiles, agencies, dealers, wtsParams) => {
  const selectedProfile = (profiles || []).find((p) => p.id === payload.profile);
  const agencyName = (agencies || []).find(
    (a) => a.id === payload.agencyCode
  )?.agency || payload.agencyCode || '';
  const dealerName = (dealers || []).find(
    (d) => d.ruc === payload.ruc
  )?.description || payload.ruc || '';

  return {
    'agency': agencyName,
    'cc_list': getMailsFromParam(wtsParams, 'SEG_INFO_MAILS_CC'),
    'dealer': dealerName,
    'email': payload.email || '',
    'groupId': selectedProfile?.role_code || '',
    'name': `${payload.names} ${payload.lastNames}`,
    'nombreGrupo': selectedProfile?.role_detail || '',
    'recipients_list': getMailsFromParam(wtsParams, 'SEG_INFO_MAILS'),
    'username': payload.user,
  };
};

const clearSaveState = (setIsSaving, setPendingPayload, setSavePhase, dispatchFn) => {
  setIsSaving(false);
  setPendingPayload(null);
  setSavePhase(null);
  dispatchFn(clearWtsUser());
};

const handleExistingKcUser = (ctx, roleCode) => {
  const {mode, pendingPayload, employeeToEdit, wtsProfiles, wtsAuth, dispatch,
    setSaveError, setIsSaving, setPendingPayload, setSavePhase} = ctx;

  if (mode === 'edit') {
    const kcUpdatePayload = {
      'agency': pendingPayload.agencyCode || null,
      'dealer': pendingPayload.ruc || null,
      'email': pendingPayload.email || '',
      'firstName': pendingPayload.names,
      'lastName': pendingPayload.lastNames,
      'status': 'A',
    };
    if (employeeToEdit.profile !== pendingPayload.profile) {
      const oldProfile = (wtsProfiles || []).find((p) => p.id === employeeToEdit.profile);
      kcUpdatePayload.oldGroupId = (oldProfile?.role_code || '').toLowerCase();
      kcUpdatePayload.newGroupId = roleCode;
    }
    dispatch(updateWtsUser(wtsAuth.user.id, kcUpdatePayload));
    setSavePhase('updating');
  } else {
    setSaveError('El usuario ya existe en Keycloak, por favor cambia el nombre de usuario');
    clearSaveState(setIsSaving, setPendingPayload, setSavePhase, dispatch);
  }
};

// eslint-disable-next-line max-statements
const handleCheckingPhase = (ctx) => {
  const {wtsAuth, pendingPayload, wtsProfiles, dispatch,
    setSaveError, setIsSaving, setPendingPayload, setSavePhase} = ctx;

  if (wtsAuth.error || hasErrorInResponse(wtsAuth.user)) {
    const checkErr = extractKcError(wtsAuth, 'Error al verificar el usuario en Keycloak');
    const isNotFoundError = typeof checkErr === 'string' &&
      (checkErr.includes('No se pudo encontrar') || checkErr.includes('not found'));

    if (!isNotFoundError) {
      setSaveError(checkErr);
      clearSaveState(setIsSaving, setPendingPayload, setSavePhase, dispatch);
      return;
    }
  }

  const selectedProfile = (wtsProfiles || []).find((p) => p.id === pendingPayload.profile);
  const roleCode = (selectedProfile?.role_code || '').toLowerCase();

  if (wtsAuth.user && wtsAuth.user.username && !hasErrorInResponse(wtsAuth.user)) {
    handleExistingKcUser(ctx, roleCode);
    return;
  }

  const kcPayload = {
    'username': pendingPayload.user,
    'email': pendingPayload.email || '',
    'name': `${pendingPayload.names} ${pendingPayload.lastNames}`,
    'password': pendingPayload.document,
    'dealer': pendingPayload.ruc || null,
    'agency': pendingPayload.agencyCode || null,
    'temporary': true,
    'rolesList': [{'groupId': roleCode}],
  };
  dispatch(createWtsUser(kcPayload));
  setSavePhase('creating');
};

const sendNotification = (ctx) => {
  const {savePhase, pendingPayload, wtsProfiles, wtsAgencies, activeDealers,
    wtsParams, currentUser, dispatch} = ctx;

  const baseNotifyData = buildBaseNotifyData(
    pendingPayload, wtsProfiles, wtsAgencies, activeDealers, wtsParams
  );

  if (savePhase === 'creating') {
    const notifyPayload = {
      'data': {
        ...baseNotifyData,
        'created_by': `${currentUser.userEmail || ''} (${currentUser.user || ''})` || 'WTS_USER',
        'temporary': true,
        'tipoEmail': 'creacion_usuario',
      },
    };
    setTimeout(() => dispatch(createUserNotify(notifyPayload)), 0);
  }

  if (savePhase === 'updating') {
    const notifyPayload = {
      'data': {
        ...baseNotifyData,
        'updated_by': `${currentUser.userEmail || ''} (${currentUser.user || ''})` || 'WTS_USER',
        'tipoEmail': 'actualizacion_usuario',
      },
    };
    setTimeout(() => dispatch(updateUserNotify(notifyPayload)), 0);
  }
};

const handleProcessPhase = (ctx) => {
  const {wtsAuth, pendingPayload, dispatch,
    setSaveError, setIsSaving, setPendingPayload, setSavePhase, finishSaveProcess} = ctx;

  if (wtsAuth.error || hasErrorInResponse(wtsAuth.user)) {
    const processErr = extractKcError(wtsAuth, 'Error al procesar el usuario en Keycloak');
    setSaveError(processErr);
    clearSaveState(setIsSaving, setPendingPayload, setSavePhase, dispatch);
    return;
  }

  const isSuccessObj = wtsAuth.user && !hasErrorInResponse(wtsAuth.user) && (
    wtsAuth.user.user_id ||
    wtsAuth.user.id ||
    (wtsAuth.user.data && wtsAuth.user.data.user_id)
  );

  if (isSuccessObj) {
    sendNotification(ctx);
    finishSaveProcess(pendingPayload);
  } else {
    setSaveError('Información incorrecta, revísela porfavor.');
    clearSaveState(setIsSaving, setPendingPayload, setSavePhase, dispatch);
  }
};

// eslint-disable-next-line max-statements
const WtsEmployeeModalGestor = ({mode, employeeToEdit, nextId}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [userStatus, setUserStatus] = useState({'message': '', 'color': 'grey', 'exists': false});
  const [docStatus, setDocStatus] = useState({'message': '', 'color': 'grey',
    'isDuplicate': false, 'isInvalid': false});
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [savePhase, setSavePhase] = useState(null);

  const currentUser = useSelector((state) => state.session || {});
  const areaSelected = useSelector((state) => state.wtsAreas?.areaSelected);
  const wtsDealers = useSelector((state) => state.wtsDealer?.dealers || []);
  const wtsPositions = useSelector((state) => state.wtsPositions?.positions || []);
  const wtsProfiles = useSelector((state) => state.wtsProfiles?.list || []);
  const wtsAgencies = useSelector((state) => state.wtsAgencies?.agencies || []);
  const allEmployees = useSelector((state) => state.wtsEmployees?.employeesByRuc || []);
  const wtsAuth = useSelector((state) => state.wtsAuth || {});
  const wtsParamsRaw = useSelector((state) => state.wtsParams);

  const wtsParams = Array.isArray(wtsParamsRaw) ? wtsParamsRaw : (wtsParamsRaw?.params || wtsParamsRaw?.data || []);

  const userDealerId = currentUser.dealerId || currentUser.ruc;

  const activeDealers = wtsDealers.filter((d) => d.status === 'ACTIVO');
  const activePositions = wtsPositions.filter((p) => p.status === 'A');
  const activeProfiles = wtsProfiles.filter((p) => p.status === 'ACTIVO');
  const activeAgencies = wtsAgencies.filter((a) => a.status === 'ACTIVO');

  const dealerOptions = getDropdownOptions(activeDealers, 'ruc', 'description') || [];
  if (userDealerId && !dealerOptions.some((o) => o.value === userDealerId)) {
    dealerOptions.push({'key': userDealerId, 'value': userDealerId, 'text': `No listado (${userDealerId})`});
  }

  const runUserValidation = (user) => {
    if (!user) {
      setUserStatus({'message': '', 'color': 'grey', 'exists': false});
      return;
    }
    const exists = allEmployees.some((emp) => emp.user === user && emp.id !== formData.id);
    setUserStatus({
      'message': exists ? 'Usuario ya existe' : 'Disponible',
      'color': exists ? 'red' : 'green',
      'exists': exists,
    });
  };

  const runDocValidation = (doc, type) => {
    if (!doc) {
      setDocStatus({'message': '', 'color': 'grey', 'isDuplicate': false, 'isInvalid': false});
      return;
    }
    const isDuplicate = allEmployees.some((emp) => emp.document === doc && emp.id !== formData.id);
    const isInvalid = !isDuplicate && type === 'C' && !validateId(doc);
    setDocStatus({
      'message': isDuplicate ? 'Ya registrada' : (isInvalid ? 'Inválida' : 'Disponible'),
      'color': isDuplicate ? 'red' : (isInvalid ? 'orange' : 'green'),
      'isDuplicate': isDuplicate,
      'isInvalid': isInvalid,
    });
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData({});
    setUserStatus({'message': '', 'color': 'grey', 'exists': false});
    setDocStatus({'message': '', 'color': 'grey', 'isDuplicate': false, 'isInvalid': false});
    setSaveError('');
    setIsSaving(false);
    setPendingPayload(null);
    setSavePhase(null);
    dispatch(clearWtsUser());
  };

  useEffect(() => {
    runUserValidation(formData.user);
    runDocValidation(formData.document, formData.documentType);
  }, [formData.user, formData.document, formData.documentType]);

  const finishSaveProcess = (payload) => {
    dispatch(changeWtsEmployeeSelected(payload));
    if (mode === 'edit') {
      dispatch(updateWtsEmployee(payload.id, payload));
    } else {
      dispatch(createWtsEmployee(payload));
    }
    if (userDealerId) {
      dispatch(getWtsEmployeesByRuc(userDealerId));
    }
    dispatch(clearWtsUser());
    setIsSaving(false);
    setPendingPayload(null);
    setSavePhase(null);
    handleClose();
  };

  useEffect(() => {
    if (!isSaving || !pendingPayload || savePhase === null) {
      return;
    }

    if (savePhase === 'finishing') {
      finishSaveProcess(pendingPayload);
      return;
    }

    if (wtsAuth.loading) {
      return;
    }

    const ctx = {
      wtsAuth, pendingPayload, mode, employeeToEdit, wtsProfiles, wtsAgencies,
      activeDealers, wtsParams, currentUser, dispatch, savePhase,
      setSaveError, setIsSaving, setPendingPayload, setSavePhase, finishSaveProcess,
    };

    if (savePhase === 'checking') {
      handleCheckingPhase(ctx);
      return;
    }

    if (savePhase === 'creating' || savePhase === 'updating') {
      handleProcessPhase(ctx);
    }
  }, [wtsAuth.loading, wtsAuth.user, wtsAuth.error, savePhase, isSaving, pendingPayload, wtsProfiles]);

  const onChangeValues = (value, field) => {
    setSaveError('');
    let val = value;
    if (isString(value)) {
      if (field === 'email') {
        val = value.toLowerCase();
      } else if (['birthDate', 'startDate'].includes(field)) {
        val = fDate(value, false);
      } else if (field !== 'user' && field !== 'id') {
        val = value.toUpperCase();
      }
    }
    if (field === 'ruc') {
      setFormData((prev) => ({...prev, 'ruc': val, 'agencyCode': ''}));
      if (areaSelected?.id && val) {
        dispatch(getWtsAgenciesDealer(areaSelected.id, val));
      }
      return;
    }
    setFormData((prev) => {
      const updated = {...prev, [field]: val};
      const isNewUser = (mode === 'new' || (mode === 'edit' && !employeeToEdit.user));
      if (isNewUser && ['names', 'lastNames'].includes(field)) {
        const n = (updated.names || '').trim().charAt(0).toLowerCase();
        const l = (updated.lastNames || '').trim().split(' ')[0].toLowerCase();
        updated.user = (n && l) ? `${n}${l}`.toUpperCase() : '';
      }
      return updated;
    });
  };

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowModal(true);
    setSaveError('');
    setIsSaving(false);
    setPendingPayload(null);
    setSavePhase(null);
    dispatch(clearWtsUser());

    if (mode === 'edit') {
      setFormData({...employeeToEdit, 'documentType': employeeToEdit?.documentType || 'C', 'ruc': userDealerId});
    } else {
      setFormData({
        'id': nextId, 'status': 'ACTIVO', 'names': '', 'lastNames': '',
        'user': '', 'documentType': 'C', 'document': '',
        'certificatesTmc': [], 'certificatesTde': [], 'ruc': userDealerId,
      });
    }

    if (areaSelected?.id) {
      dispatch(getWtsPositions(areaSelected.id));
      dispatch(getWtsProfiles(areaSelected.id));
      dispatch(getWtsParams({'area': areaSelected.id}));
    }
  };

  const isLocked = mode === 'edit' && (
    (Array.isArray(formData.certificatesTmc) && formData.certificatesTmc.length > 0) ||
    (Array.isArray(formData.certificatesTde) && formData.certificatesTde.length > 0)
  );

  const isFormValid = !!(
    formData.names?.trim() &&
    formData.lastNames?.trim() &&
    formData.document?.trim() &&
    formData.user?.trim() &&
    !userStatus.exists &&
    !docStatus.isDuplicate &&
    (!docStatus.isInvalid || isLocked)
  );

  const saveEmployee = (e) => {
    e.preventDefault();
    if (!isFormValid || isSaving) {
      return;
    }
    setSaveError('');

    const payload = {
      ...formData,
      'birthDate': fDate(formData.birthDate, false),
      'startDate': fDate(formData.startDate, false),
      'ruc': userDealerId,
    };

    setIsSaving(true);
    setPendingPayload(payload);

    const selectedProfile = (wtsProfiles || []).find((p) => p.id === payload.profile);
    const isTechnical = selectedProfile?.description === 'TÉCNICO';

    if (isTechnical) {
      setSavePhase('finishing');
    } else {
      setSavePhase('checking');
      dispatch(getWtsUser(payload.user));
    }
  };

  const emailOptions = (typeof formData.email === 'string' && formData.email.includes('@')) ?
    ['tde.com.ec', 'gmail.com', 'hotmail.com'].map((d) => (`${formData.email.split('@')[0]}@${d}`)) : [];

  return (
    <React.Fragment>
      {mode === 'new' ? (
        <Button compact content="Añadir" icon={<Icon name="add-circle" size="big" color="green" />}
          onClick={handleOpen} />
      ) : (
        <Icon link name="pencil" size="big" onClick={handleOpen} color="green" />
      )}

      <Modal size="large" closeOnEscape={false} closeOnDimmerClick={false} open={showModal} onClose={handleClose}>
        <Modal.Header>
          <div className="header flex">
            <h2 className="flex grow" style={{'margin': 0}}>{mode === 'new' ? 'Crear' : 'Editar'}
               Participante (Gestor Dealer)</h2>
            <Icon name="close" style={{'margin': 0, 'cursor': 'pointer'}} onClick={handleClose} />
          </div>
        </Modal.Header>
        {saveError && (
          <Message error style={{'margin': '0 1em'}} onDismiss={() => setSaveError('')}>
            <Message.Header>Error</Message.Header>
            <p>{saveError}</p>
          </Message>
        )}
        <Modal.Content>
          <Grid stackable stretched>
            <Grid.Row>
              <Grid.Column width={8}><Input fluid label="Código" value={formData.id || ''} disabled /></Grid.Column>
              <Grid.Column width={8}>
                <Input fluid label="Usuario" value={formData.user || ''}
                  disabled={mode === 'edit' && !!employeeToEdit?.user}
                  onChange={(e, {value}) => onChangeValues(value.toUpperCase(), 'user')} />
                {!(mode === 'edit' && !!employeeToEdit?.user) && userStatus.message && (
                  <Label basic color={userStatus.color} pointing>{userStatus.message}</Label>
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}><Input fluid label="Nombres" value={formData.names || ''} disabled={isLocked}
                onChange={(e, {value}) => onChangeValues(value, 'names')} /></Grid.Column>
              <Grid.Column width={8}><Input fluid label="Apellidos" value={formData.lastNames || ''} disabled={isLocked}
                onChange={(e, {value}) => onChangeValues(value, 'lastNames')} /></Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <Input fluid labelPosition="left" placeholder="Documento" value={formData.document || ''}
                  disabled={isLocked}
                  label={(
                    <Dropdown options={[
                      {'key': 'C', 'text': 'Cédula', 'value': 'C'},
                      {'key': 'P', 'text': 'Pasaporte', 'value': 'P'},
                    ]}
                    value={formData.documentType || 'C'} disabled={isLocked}
                    onChange={(e, {value}) => onChangeValues(value, 'documentType')} />
                  )}
                  onChange={(e, {value}) => onChangeValues(value, 'document')} />
                {!isLocked && docStatus.message &&
                  <Label basic color={docStatus.color} pointing>{docStatus.message}</Label>}
              </Grid.Column>
              <Grid.Column width={8}><Input fluid type="date" label="Nacimiento" value={fDate(formData.birthDate, true)}
                onChange={(e, {value}) => onChangeValues(value, 'birthDate')} /></Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}><Input fluid type="date" label="Contrato" value={fDate(formData.startDate, true)}
                onChange={(e, {value}) => onChangeValues(value, 'startDate')} /></Grid.Column>
              <Grid.Column width={8}>
                <Input fluid label="Email" list="emails" value={formData.email || ''}
                  onChange={(e, {value}) => onChangeValues(value, 'email')} />
                <datalist id="emails">{emailOptions.map((o) => (
                  <option key={o} value={o} />
                ))}</datalist>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}><div className="ui label">Cargo</div>
                <Dropdown fluid selection value={formData.charge || ''}
                  onChange={(e, i) => onChangeValues(i.value, 'charge')}
                  options={getDropdownOptions(activePositions, 'code', 'code') || []} /></Grid.Column>
              <Grid.Column width={8}><div className="ui label">Perfil</div>
                <Dropdown fluid selection value={formData.profile || ''}
                  onChange={(e, i) => onChangeValues(i.value, 'profile')}
                  options={getDropdownOptions(activeProfiles, 'id', 'description') || []} /></Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}><div className="ui label">Concesionario</div>
                <Dropdown fluid selection disabled value={formData.ruc || userDealerId || ''}
                  onChange={(e, i) => onChangeValues(i.value, 'ruc')}
                  options={dealerOptions} /></Grid.Column>
              <Grid.Column width={8}><div className="ui label">Agencia</div>
                <Dropdown fluid selection disabled={!formData.ruc && !userDealerId} value={formData.agencyCode || ''}
                  onChange={(e, i) => onChangeValues(i.value, 'agencyCode')}
                  options={getDropdownOptions(uniqBy(activeAgencies, 'id'), 'id', 'agency') || []} /></Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <div className="ui label" style={{'marginBottom': '4px'}}>Certificaciones TOYOTA (TMC)</div>
                <div style={{'border': '1px solid rgba(34, 36, 38, 0.15)',
                  'borderRadius': '0.285714rem', 'padding': '0.785714em 1em',
                  'minHeight': '38px', 'backgroundColor': '#f9fafb'}}>
                  {Array.isArray(formData.certificatesTmc) && formData.certificatesTmc.length > 0 ? (
                    formData.certificatesTmc.map((cert, idx) => (
                      <div key={idx} style={{'marginBottom': idx < formData.certificatesTmc.length - 1 ? '5px' : '0'}}>
                        <Icon name="check circle" color="green" />
                        <strong>{cert.certificate}</strong>
                        <span style={{'color': 'gray'}}>- {fDate(cert.date, false)}</span>
                      </div>
                    ))
                  ) : <span style={{'color': 'gray'}}>Sin certificaciones registradas</span>}
                </div>
              </Grid.Column>
              <Grid.Column width={8}>
                <div className="ui label" style={{'marginBottom': '4px'}}>Certificaciones TDE</div>
                <div style={{'border': '1px solid rgba(34, 36, 38, 0.15)',
                  'borderRadius': '0.285714rem', 'padding': '0.785714em 1em',
                  'minHeight': '38px', 'backgroundColor': '#f9fafb'}}>
                  {Array.isArray(formData.certificatesTde) && formData.certificatesTde.length > 0 ? (
                    formData.certificatesTde.map((cert, idx) => (
                      <div key={idx} style={{'marginBottom': idx < formData.certificatesTde.length - 1 ? '5px' : '0'}}>
                        <Icon name="check circle" color="blue" />
                        <strong>{cert.certificate}</strong>
                        <span style={{'color': 'gray'}}>- {fDate(cert.date, false)}</span>
                      </div>
                    ))
                  ) : <span style={{'color': 'gray'}}>Sin certificaciones registradas</span>}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button positive compact icon="save" loading={isSaving} onClick={saveEmployee}
            disabled={!isFormValid || isSaving}>Guardar</Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
};

export default WtsEmployeeModalGestor;
